import { useMemo } from 'react';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';
import { getMonthlyRecurringCharges } from '../../utils/lease-helpers';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { formatADA, formatAccessibilityTypes } from '../../utils/ada-helpers';
import {
  getUnitMoveInReadyDate,
  getUnitMoveOutDate,
} from '../AllUnitAvailability/utils';

const useParseResults = ({ results, propertyPrimaryHUDCode, fromUnit }) => {
  return useMemo(() => {
    const today = moment();
    const { units, transferUnit } = results;
    const filteredUnits = isEmpty(fromUnit ?? '')
      ? units
      : units.filter((unit) => unit.number !== fromUnit);
    return filteredUnits.map((unit) => {
      const {
        floorPlan,
        currentAssignation,
        nextAssignation,
        priorAssignation,
        unitStatus,
        unitFees,
        building,
      } = unit;
      const { nBeds, nBaths, nHalfBaths } = floorPlan;
      const { isAvailable, isVacant } = unitStatus;
      const { lease, application } = currentAssignation;
      const isTransfer = nextAssignation?.lease?.isTransfer;
      const household = lease?.household || application?.household;
      const bedsBaths = `${+nBeds}/${+nBaths + +nHalfBaths * 0.5}`;
      const turnMoveOutDays = unit?.property?.turnMoveOutDays ?? 0;
      const lastVacancyStartDate = isVacant ? unit?.lastVacancyStartDate : null;
      // ENG-30316: the real prior-occupancy move-out, kept separate from the displayed
      // `moveOutDate` below. `getUnitMoveOutDate` falls back to `lastVacancyStartDate`, which is a
      // unit-status marker rather than evidence a household lived here, so the transfer-overlap
      // rule must not see it. DATEONLY, so this stays a 'YYYY-MM-DD' string.
      const priorOccupancyMoveOutDate =
        currentAssignation?.lease?.noticeToVacate?.moveOutDate ??
        priorAssignation?.lease?.moveOutDate ??
        null;
      const moveOutDate = getUnitMoveOutDate(
        lastVacancyStartDate,
        priorOccupancyMoveOutDate,
      );
      const {
        firstName,
        lastName,
        rc: { residentId },
      } = household?.mainCus || { rc: {} };
      const effectiveFloorPlan = unit.scheduledFloorPlan ?? floorPlan;

      return {
        originalUnit: unit,
        assignedApplicationId: nextAssignation?.application?.id,
        nBeds,
        buildingId: building?.id,
        building: building?.name,
        unitId: unit.id,
        unit: unit.number,
        unitLink: getUrlWithSelectedPropertyId(
          `/unit/${unit.id}?prev=available`,
        ),
        fpPap: floorPlan?.fpap ?? [],
        transferFpPap: transferUnit?.floorPlan?.fpap ?? [],
        floorPlan: floorPlan.internalName,
        floorPlanLink: getUrlWithSelectedPropertyId(
          `/floorplan/${floorPlan.id}`,
        ),
        isFloorPlanAffordable: effectiveFloorPlan.isAffordable,
        isFloorPlanCommercial:
          effectiveFloorPlan.isCommercial &&
          effectiveFloorPlan.isCommercialLeasingActive,
        /*
         * ENG-32373: must match how the backend derives `currentUnitHudCode`
         * (Affordables.js getHUDTransferProps) — the FIRST fpap row THAT HAS a hudCode, not the
         * first row. The include carries no `order`, so a floor plan layered HUD + LIHTC can hand
         * us the LIHTC row (hudCode null) first; reading `[0]` then fell through to the property
         * primary code and made a unit look like a different HUD type than its own floor plan.
         */
        floorPlanHudCode:
          floorPlan?.fpap?.find((program) => program?.hudCode)?.hudCode ??
          propertyPrimaryHUDCode,
        bedsBaths,
        sqFeet: floorPlan.grossSquareFeet,
        status: unitStatus.description,
        leased: (() => {
          const hasApplicant = Boolean(nextAssignation?.id);
          const { application, lease } = nextAssignation;
          const status = application?.applicationStatus?.name;
          const isTransferring = lease?.isTransfer;

          if (
            status === 'Converted to Resident' &&
            currentAssignation &&
            !isTransferring
          ) {
            return false;
          }

          return hasApplicant;
        })(),
        resident: (() => {
          if (!currentAssignation) {
            return '';
          }

          let name;

          if (firstName && lastName) {
            name = `${lastName}, ${firstName}`;
          } else {
            name = firstName + lastName;
          }

          const isUnitCommercial =
            floorPlan?.isCommercial && floorPlan?.isCommercialLeasingActive;
          if (isUnitCommercial) {
            name = firstName;
          }

          return name;
        })(),
        residentLink: getUrlWithSelectedPropertyId(`/resident/${residentId}`),
        residentId,
        marketRent: unit.rentCalculations.marketRent,
        netMaxRent: unit.rentCalculations.netMaxRent,
        priorLeasedRent: priorAssignation?.lease?.leasedRent,
        currentLeasedRent: (() => {
          let rent = 0;
          if (!isVacant) {
            rent = currentAssignation?.lease?.leasedRent || 0;
          } else {
            const applicantStatus =
              nextAssignation?.application?.applicationStatus?.name || '';
            if (applicantStatus.includes('Approved')) {
              rent = nextAssignation?.lease?.leasedRent || 0;
            }
          }

          return rent;
        })(),
        moveOutDate,
        priorOccupancyMoveOutDate,
        moveInReady: isAvailable
          ? getUnitMoveInReadyDate(
              unitStatus.description,
              unit.makeReadyDate ?? null,
              turnMoveOutDays,
              moveOutDate,
            )
          : null,
        notes: unit.notes,
        daysVacant: (() => {
          if (!isAvailable || !isVacant) {
            return null;
          }

          const moveOut = priorAssignation?.lease?.moveOutDate;
          const lastVacancyStartDate = unit?.lastVacancyStartDate;

          if (
            !moveOut &&
            unitStatus?.isAvailable &&
            isVacant &&
            lastVacancyStartDate
          ) {
            return moment().diff(moment(lastVacancyStartDate), 'days');
          }

          if (moment(moveOut).isAfter(today)) {
            return null;
          }

          return moment().diff(moment(moveOut), 'days');
        })(),
        quotingRent: (() => {
          if (!isAvailable) {
            return null;
          }

          // Use the calculated quotingRent from rentCalculations if available,
          // otherwise fall back to unit quotingRentAmount, then floorplan
          const calculatedQuotingRent = unit.rentCalculations?.quotingRent;

          if (calculatedQuotingRent != null) {
            return calculatedQuotingRent;
          }

          let startingRent = unit.quotingRentAmount;
          // Only fall back to floorplan if unit quotingRentAmount is null/undefined
          // Allow 0 as a valid unit-level quoting rent amount
          if (startingRent == null) {
            startingRent = floorPlan.quotingRentAmount;
            if (startingRent == null) {
              return null;
            }
          }

          // Return the quoting rent amount directly without fee calculations
          return startingRent;
        })(),
        otherUnitFeesAvailable: (() => {
          const arr = getMonthlyRecurringCharges(unit);

          if (!arr.length) {
            return null;
          }

          return arr.reduce((prev, fee) => {
            prev += fee.value;
            return prev;
          }, 0);
        })(),
        ...(() => {
          const result = { applicant: null, applicantLink: null };
          if (!nextAssignation?.application) {
            return result;
          }

          const applicationStatus =
            nextAssignation.application.applicationStatus?.name || '';

          if (
            applicationStatus === 'Converted to Resident' &&
            currentAssignation &&
            !isTransfer
          ) {
            return result;
          }

          const customer =
            nextAssignation.lease?.household?.mainCus ||
            nextAssignation.application.household.mainCus;

          if (!customer) {
            return result;
          }

          const applicationId = nextAssignation.application.id || '';
          result.applicant =
            customer.lastName && customer.firstName
              ? `${customer.lastName}, ${customer.firstName}`
              : customer.firstName + customer.lastName;

          const isUnitCommercial =
            floorPlan?.isCommercial && floorPlan?.isCommercialLeasingActive;
          if (isUnitCommercial) {
            result.applicant = customer.firstName || '';
          }

          const residentId =
            nextAssignation.lease?.household?.mainCus?.rc?.residentId ||
            nextAssignation?.application?.household?.mainCus?.rc?.residentId;

          result.applicantLink = getUrlWithSelectedPropertyId(
            isTransfer
              ? `/resident/${residentId}`
              : `/application/${applicationId}`,
          );

          return result;
        })(),
        applicantStatus: (() => {
          let applicantStatus =
            nextAssignation?.application?.applicationStatus?.name;

          if (applicantStatus === 'Converted to Resident') {
            if (!currentAssignation) {
              applicantStatus = 'Awaiting Move In';
            } else if (isTransfer && currentAssignation) {
              const unitNumber =
                nextAssignation?.lease?.household?.activeLease?.units?.[0]
                  ?.number ||
                nextAssignation?.application?.household?.activeLease?.units?.[0]
                  ?.number ||
                '';
              applicantStatus = `Transferring from ${unitNumber}`;
            }
          }

          return applicantStatus;
        })(),
        scheduledMoveInDate: (() => {
          const status = nextAssignation?.application?.applicationStatus?.name;
          const isMovedIn = nextAssignation?.lease?.isMovedIn ?? true;
          const moveInDate = nextAssignation?.lease?.moveInDate;

          if (
            status === 'Converted to Resident' &&
            currentAssignation &&
            !isTransfer
          ) {
            return null;
          }

          if (isMovedIn || !moveInDate) {
            return null;
          }

          return moveInDate;
        })(),
        amenities: unitFees.length
          ? unitFees
              .map(({ propertyFees }) => {
                return propertyFees?.displayNameOnQuote;
              })
              .join(', ')
          : null,
        ada: formatADA(unit.isADA),
        accessibilityType: formatAccessibilityTypes(
          unit.isADA,
          unit.accessibilityTypes,
        ),
      };
    });
  }, [fromUnit, propertyPrimaryHUDCode, results]);
};

export default useParseResults;
